import { Audience, AudienceFilter, AudienceFilterApp } from '../../audience/components/audience.model';

/**
 * Source detection for the new audience hub. Each audience can pull from our
 * internal data, from one or more 3rd-party integrations, or a mix. The list
 * view renders a small chip per distinct source.
 *
 * Detection is filter-based: a filter without `filter.app` is treated as
 * internal. A filter with `filter.app` set contributes that 3rd-party source.
 * Session conditions are always tracker-side (internal) and do not contribute
 * new sources.
 */

export type AudienceSourceKind = 'internal' | AudienceFilterApp;

/** Triggerbee's own brand glyph, shown for internal (first-party) sources. */
const TRIGGERBEE_LOGO = 'assets/img/logos/triggerbee-black-bullet.png';

export interface AudienceSource {
  /** Internal flag or the specific 3rd-party app. */
  kind: AudienceSourceKind;
  /** Human-readable label for the chip. */
  label: string;
  /** Path to the brand logo for 3rd-party sources, undefined for internal. */
  logoUrl?: string;
}

/**
 * App → logo asset path. Keep aligned with files under `src/assets/img/logos/`.
 */
const APP_LOGO: Record<AudienceFilterApp, string> = {
  [AudienceFilterApp.Rule]: 'assets/img/logos/rule-logo.png',
  [AudienceFilterApp.Mailchimp]: 'assets/img/logos/mailchimp_freddie_color_web.png',
  [AudienceFilterApp.Voyado]: 'assets/img/logos/voyado.png',
  [AudienceFilterApp.Klaviyo]: 'assets/img/logos/klaviyo.svg',
  [AudienceFilterApp.Emarsys]: 'assets/img/logos/emarsys.png',
  [AudienceFilterApp.Custobar]: 'assets/img/logos/custobar.png',
  [AudienceFilterApp.MailerLite]: 'assets/img/logos/mailerlite.png',
  [AudienceFilterApp.Shopify]: 'assets/img/logos/shopify_glyph.png',
  [AudienceFilterApp.Brevo]: 'assets/img/logos/brevo.jpg',
  [AudienceFilterApp.Dotdigital]: 'assets/img/logos/dotdigital.png',
  [AudienceFilterApp.Omnisend]: 'assets/img/logos/omnisend.png',
  [AudienceFilterApp.CleverReach]: 'assets/img/logos/cleverreach.svg',
  [AudienceFilterApp.Shopware]: 'assets/img/logos/shopware.svg',
  // `Custom` is a generic catch-all app type with no specific logo —
  // sources from it fall back to the internal icon.
  [AudienceFilterApp.Custom]: '',
};

/**
 * Returns the distinct sources contributing to an audience. Order: internal first,
 * then 3rd-party apps in the order they appear in the filter list.
 */
export function getAudienceSources(audience: Audience | null | undefined): AudienceSource[] {
  if (!audience) return [];
  const filters = audience.filters ?? [];
  const seen = new Set<AudienceSourceKind>();
  const sources: AudienceSource[] = [];

  // Default audiences with no filters are always internal.
  if (filters.length === 0) {
    return [{ kind: 'internal', label: 'Triggerbee', logoUrl: TRIGGERBEE_LOGO }];
  }

  for (const filter of filters) {
    const kind = filterSource(filter);
    if (seen.has(kind)) continue;
    seen.add(kind);
    if (kind === 'internal') {
      sources.push({ kind, label: 'Triggerbee', logoUrl: TRIGGERBEE_LOGO });
    } else {
      sources.push({ kind, label: kind, logoUrl: APP_LOGO[kind] });
    }
  }

  return sources;
}

function filterSource(filter: AudienceFilter): AudienceSourceKind {
  if (filter.app) return filter.app;
  return 'internal';
}

/**
 * Convenience: true if the audience only uses internal data (no 3rd-party filter).
 */
export function isPureInternal(audience: Audience): boolean {
  const sources = getAudienceSources(audience);
  return sources.length === 1 && sources[0].kind === 'internal';
}
